import { APIResponse } from 'common/api/models';
import { SUGGEST_API_URL } from 'common/api/search/config';
import { apiFetch } from 'common/api/utils';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { SearchGrid } from 'components/search-grid/SearchGrid';
import React, { useState, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import EmptyResults from '../EmptyResults';
import { CardContext } from './contexts';
import { useSearchGridStyles } from './hooks';
import { TopPicksCard } from './TopPicksCard';

interface Suggestion {
    '@search.text': string;
    OrganizationId: string;
    Name: string;
    NormalizedName: string;
    Categories: string[];
    Price: number[];
    Rebate: number[];
    MaxRebate: number;
    IsOnBonus: boolean;
    SupportedTypes: string[];
    IsFeatured: boolean;
    Lists: string[];
    KeyWords: string[];
    PopularityRank: number;
    ProductIds: number[];
    ImageUrl: string;
    BlacklistedStates: string[];
    IsLocalProduct: boolean;
    Suggestions: string[];
    IsShipToHomeEligible: boolean;
    UrlSlug: string;
    NewBannerExpiration: Date | null;
    Id: string;
}

export const suggestionsToBrands = (suggestions: Suggestion[]): Brand[] => {
    return suggestions.map((suggestion) => {
        return {
            id: suggestion.Id,
            name: suggestion.Name,
            rebate: suggestion.MaxRebate,
            isOnBonus: suggestion.IsOnBonus,
            isFeatured: suggestion.IsFeatured,
            imageUrl: suggestion.ImageUrl,
            keyWords: suggestion.KeyWords,
            categories: suggestion.Categories,
            productIds: suggestion.ProductIds,
            supportedTypes: suggestion.SupportedTypes,
            suggestions: suggestion.Suggestions,
            urlSlug: suggestion.UrlSlug,
            newBannerExpiration: suggestion.NewBannerExpiration
                ? new Date(suggestion.NewBannerExpiration)
                : null,
            isLocalProduct: false,
            isNew: suggestion.NewBannerExpiration
                ? new Date() < suggestion.NewBannerExpiration
                : false,
        } as Brand;
    });
};

interface Props {
    searchTerm: string;
}

const cache: { [key: string]: Suggestion[] } = {};
const cacheSize = 10;

const SuggestionsBySearchTerm = ({ searchTerm }: Props) => {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [, width] = useSearchGridStyles<'150', '180'>('150', '180');

    const memoizedSuggestions = useMemo(() => suggestionsToBrands(suggestions), [suggestions]);

    useEffect(() => {
        if (searchTerm in cache) {
            setSuggestions(cache[searchTerm]);
        } else {
            const getSuggestions = async () => {
                setLoading(true);
                const url = SUGGEST_API_URL;
                const payload = {
                    fuzzy: true,
                    suggesterName: 'BrandSuggester',
                    select: '*',
                    search: searchTerm,
                    top: 10,
                    searchFields: 'Name,KeyWords,Suggestions',
                    filter: '(OrganizationId eq null)',
                };

                try {
                    const { data } = await apiFetch<APIResponse<Suggestion[]>>(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    });

                    setSuggestions(data?.data || []);
                    cache[searchTerm] = data?.data || [];
                    if (Object.keys(cache).length > cacheSize) {
                        delete cache[Object.keys(cache)[0]];
                    }
                } finally {
                    setLoading(false);
                }
            };

            if (searchTerm) {
                getSuggestions();
            }
        }
    }, [searchTerm]);

    if (loading) {
        <Skeleton className="h-80" />;
    }

    if (!suggestions.length) {
        return <EmptyResults isConstructorEnabled={false} term={searchTerm} />;
    }

    return (
        <div className="mb-20">
            <CardContext.Provider value={{ width }}>
                <SearchGrid
                    ItemComponent={TopPicksCard}
                    items={memoizedSuggestions}
                    isLoading={loading}
                />
            </CardContext.Provider>
        </div>
    );
};

export default SuggestionsBySearchTerm;
